import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, RefreshCw } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BottomNav from "../components/BottomNav";

export default function Selection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/selection");
        if (!active) return;
        setProfiles(data.profiles || []);
        setUpdatedAt(data.updated_at || null);
      } catch (e) {}
      if (active) setLoading(false);
    };
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    return () => { active = false; clearInterval(iv); };
  }, []);

  const openProfile = (id) => {
    if (!user?.premium) {
      navigate("/premium");
      return;
    }
    navigate(`/decouvrir?user=${id}`);
  };

  return (
    <div className="min-h-screen bg-kora-sand pb-28">
      <div className="max-w-md mx-auto px-5 pt-6">
        <div data-testid="selection-header" className="animate-kora-pulse bg-gradient-to-br from-kora-ink to-[#4a382b] rounded-3xl p-5 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-36 h-36 bg-kora-terracotta/40 rounded-full blur-2xl animate-kora-blob" />
          <div className="absolute -bottom-10 -left-6 w-32 h-32 bg-kora-ochre/30 rounded-full blur-2xl animate-kora-blob-2" />
          <div className="absolute top-6 left-1/2 w-24 h-24 bg-[#E05D36]/25 rounded-full blur-2xl animate-kora-blob" style={{ animationDelay: "1.5s" }} />
          <div className="relative">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-kora-terracotta to-kora-ochre flex items-center justify-center shrink-0 animate-kora-pulse">
                <Crown size={20} className="text-white" />
              </div>
              <h1 data-testid="selection-title" className="font-display font-black text-2xl text-white">Sélection du jour</h1>
            </div>
            <p data-testid="selection-description" className="text-[13px] text-white/80 mt-3 leading-relaxed">
              Découvrez les profils les plus actifs du jour.
              La liste est mise à jour automatiquement toutes les 15 minutes pour vous proposer les membres les plus présents du moment.
            </p>
            {updatedAt && (
              <p className="text-[11px] text-kora-ochre mt-2.5 inline-flex items-center gap-1 font-semibold">
                <RefreshCw size={11} /> Dernière mise à jour : {new Date(updatedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
          </div>
        </div>
        {loading ? (
          <div className="mt-10 text-center text-kora-muted text-sm">Chargement…</div>
        ) : profiles.length === 0 ? (
          <div data-testid="selection-empty" className="mt-10 bg-white rounded-3xl border border-kora-line p-8 text-center">
            <p className="font-display font-bold text-lg text-kora-ink">Pas encore de sélection</p>
            <p className="text-sm text-kora-muted mt-1">Aucun membre actif pour le moment aujourd'hui. Revenez un peu plus tard !</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 mt-5">
            {profiles.map((p, i) => (
              <button
                key={p.user_id}
                data-testid={`selection-profile-${p.user_id}`}
                onClick={() => openProfile(p.user_id)}
                className="animate-kora-fade-up bg-white rounded-3xl border border-kora-line p-4 flex flex-col items-center hover:border-kora-terracotta transition-colors"
                style={{ animationDelay: `${0.1 + i * 0.07}s` }}
              >
                <span className="relative">
                  {p.photo_url ? (
                    <img src={resolveImg(p.photo_url)} alt={p.name} className="w-20 h-20 rounded-full object-cover border-[3px] border-kora-terracotta" />
                  ) : (
                    <span className="w-20 h-20 rounded-full bg-kora-terracotta border-[3px] border-kora-ochre flex items-center justify-center text-white text-2xl font-black">{(p.name || "?")[0]}</span>
                  )}
                  <span className="absolute -top-1 -left-1 w-6 h-6 rounded-full bg-kora-ink text-kora-ochre text-[11px] font-black flex items-center justify-center border-2 border-white">{i + 1}</span>
                </span>
                <p className="font-display font-bold text-kora-ink mt-2.5 truncate w-full text-center">{p.name}</p>
                <p className="text-xs text-kora-muted truncate w-full text-center">{p.residence_country || "—"}</p>
              </button>
            ))}
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
